/**
 * <om-bundle-builder>: the "Add matching items" popup of the product page
 * (see snippets/om-bundle-builder.liquid).
 *
 * The page keeps Dawn's plain Add-to-cart button. The first time it (or the
 * sticky bar's button) is pressed, the submit is taken over here: this product
 * is added to the cart, then the popup opens instead of the cart drawer and
 * offers the other products of the same design as checkbox rows (own color and
 * size, "See photos" viewer) with the store's buy-2 / buy-3 offers. Ticked
 * items are added in a second request. Every way out of the popup ("No
 * thanks", the close button, Esc, a tap outside, or adding items) ends by
 * opening the cart drawer with the up-to-date cart.
 *
 * The popup shows once per page view, and not at all when the cart already
 * holds one of the matching products; then Dawn's own flow runs untouched.
 *
 * <om-proof-quote>: rotating customer quote under the rating (defined below).
 *
 * <om-parts-checklist>: the tap-your-replaced-parts report
 * (see sections/om-parts-checklist.liquid).
 */
if (!customElements.get('om-bundle-builder')) {
  class OmBundleBuilder extends HTMLElement {
    connectedCallback() {
      // A modal must not sit inside the product column (its ancestors carry
      // transforms and spacing rules). Re-parent to <body> once; the move
      // re-enters connectedCallback.
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
        return;
      }
      if (this.ready) return;
      this.ready = true;

      this.sectionId = this.dataset.sectionId;
      this.discountCount = parseInt(this.dataset.discountCount, 10) || 0;
      this.discountPercent = parseFloat(this.dataset.discountPercent) || 0;
      this.mainVariants = this.readVariants(this.querySelector('[data-om-main]'));
      this.items = Array.from(this.querySelectorAll('[data-om-item]')).map((el) => this.setupItem(el));
      this.offers = Array.from(this.querySelectorAll('[data-om-offer]'));
      this.savingEl = this.querySelector('[data-om-saving]');
      this.errorEl = this.querySelector('[data-om-error]');
      this.dialog = this.querySelector('[data-om-upsell]');
      this.addButton = this.querySelector('[data-om-upsell-add]');
      this.busy = false;
      this.shown = false; // once per page view
      this.cartHasMatch = false; // a matching product is already in the cart
      this.pending = null; // { cart, response }: what the drawer shows once the popup closes
      this.mainPrice = this.mainVariants[0]?.price || 0;

      // No <dialog> support: leave the page's normal add-to-cart flow alone.
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

      const productIds = this.items.map((item) => Number(item.el.dataset.omItem));
      const root = window.Shopify?.routes?.root || '/';
      fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          this.cartHasMatch = (cart.items || []).some((line) => productIds.includes(line.product_id));
        })
        .catch(() => {
          /* unknown cart: offer the popup */
        });

      // Capture on the document so this runs before Dawn's own submit listener
      // on the main form and on the sticky bar's form.
      this.onFormSubmit = this.onFormSubmit.bind(this);
      document.addEventListener('submit', this.onFormSubmit, true);

      this.addButton?.addEventListener('click', () => this.addExtras());
      this.querySelectorAll('[data-om-upsell-close]').forEach((button) =>
        button.addEventListener('click', () => this.closeUpsell())
      );
      // A tap on the dark area around the popup is a "no thanks" too.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.closeUpsell();
      });
      // Esc. The dialog's own `close` event is delivered with the next painted
      // frame, which a background tab may never get, so every exit goes through
      // closeUpsell() directly and `close` is only a safety net.
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeUpsell();
      });
      this.dialog.addEventListener('close', () => this.afterUpsell());

      this.refresh();
    }

    disconnectedCallback() {
      if (!this.ready) return;
      document.removeEventListener('submit', this.onFormSubmit, true);
      this.ready = false;
    }

    // ---- setup ----
    readVariants(scope) {
      try {
        return JSON.parse(scope?.querySelector('[data-om-variants]')?.textContent || '[]');
      } catch (e) {
        return [];
      }
    }

    setupItem(el) {
      const item = {
        el,
        title: el.dataset.omTitle || '',
        variants: this.readVariants(el),
        options: Array.from(el.querySelectorAll('[data-om-option]')),
        optionsBox: el.querySelector('[data-om-options]'),
        toggle: el.querySelector('[data-om-toggle]'),
        priceEl: el.querySelector('[data-om-price]'),
        imageEl: el.querySelector('[data-om-image]'),
        soldOutEl: el.querySelector('[data-om-soldout]'),
        chooseEl: el.querySelector('[data-om-choose]'),
        active: false,
      };

      // These inputs live inside the main product column; keep the page's own
      // variant picker and analytics listeners from reacting to them.
      item.options.forEach((option) =>
        option.addEventListener('change', (event) => {
          event.stopPropagation();
          this.onOptionChange(item);
        })
      );
      item.toggle?.addEventListener('change', (event) => {
        event.stopPropagation();
        this.setActive(item, item.toggle.checked);
      });
      this.setupViewer(item);
      return item;
    }

    // ---- photo viewer ----
    // Each matching item has its own design, so its photos open on this page
    // (native <dialog>: Esc and the Close button dismiss it, the page behind is inert).
    setupViewer(item) {
      const dialog = item.el.querySelector('[data-om-viewer]');
      if (!dialog || typeof dialog.showModal !== 'function') {
        // No <dialog> support: the photo buttons would do nothing, so hide them.
        item.el.querySelectorAll('.om-bundle__view').forEach((button) => (button.hidden = true));
        return;
      }
      const track = dialog.querySelector('[data-om-viewer-track]');
      const slides = Array.from(dialog.querySelectorAll('[data-om-viewer-slide]'));
      if (!track || !slides.length) return;

      const count = dialog.querySelector('[data-om-viewer-count]');
      let index = 0;
      const label = () => (count.textContent = `Photo ${index + 1} of ${slides.length}`);
      // The track is a native scroll-snap strip, so a finger swipe moves it the
      // same way the product gallery does; the arrows just scroll it for you.
      const show = (next, smooth = true) => {
        index = (next + slides.length) % slides.length;
        track.scrollTo({ left: slides[index].offsetLeft - track.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
        label();
      };
      // After a swipe: the slide nearest the left edge is the current one.
      let ticking = false;
      track.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(() => {
            ticking = false;
            const left = track.scrollLeft + track.offsetLeft;
            index = slides.reduce(
              (best, slide, i) => (Math.abs(slide.offsetLeft - left) < Math.abs(slides[best].offsetLeft - left) ? i : best),
              0
            );
            label();
          });
        },
        { passive: true }
      );
      const single = slides.length < 2;
      dialog.querySelectorAll('.om-viewer__nav').forEach((button) => (button.hidden = single));

      const fileOf = (url) => (url || '').split('?')[0].split('/').pop();
      item.el.querySelectorAll('[data-om-view]').forEach((button) =>
        button.addEventListener('click', () => {
          dialog.showModal();
          document.documentElement.classList.add('om-viewer-open');
          // Start on the picture of the color shown in the row, when it is in the strip.
          const current = fileOf(item.imageEl?.src);
          const start = slides.findIndex((slide) => fileOf(slide.querySelector('img')?.getAttribute('src')) === current);
          show(start === -1 ? 0 : start, false);
        })
      );
      dialog.querySelector('[data-om-viewer-prev]').addEventListener('click', () => show(index - 1));
      dialog.querySelector('[data-om-viewer-next]').addEventListener('click', () => show(index + 1));
      const closeViewer = () => {
        if (dialog.open) dialog.close();
        document.documentElement.classList.remove('om-viewer-open');
      };
      dialog.querySelector('[data-om-viewer-close]').addEventListener('click', closeViewer);
      dialog.querySelector('[data-om-viewer-add]')?.addEventListener('click', () => {
        closeViewer();
        this.setActive(item, true);
        const first = item.optionsBox?.querySelector('select, input');
        item.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        first?.focus({ preventScroll: true });
      });
      // A tap on the dark area around the panel closes it too.
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeViewer();
      });
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') show(index - 1);
        if (event.key === 'ArrowRight') show(index + 1);
      });
      dialog.addEventListener('close', () => document.documentElement.classList.remove('om-viewer-open'));
    }

    // ---- state ----
    optionValues(item) {
      return item.options.map((option) => {
        const select = option.querySelector('select');
        if (select) return select.value;
        return option.querySelector('input[type="radio"]:checked')?.value;
      });
    }

    variantOf(item) {
      if (!item.options.length) return item.variants[0];
      const values = this.optionValues(item);
      return item.variants.find((variant) => variant.options.every((value, index) => value === values[index]));
    }

    // Options the shopper still has to choose (size dropdowns start empty).
    missingOptions(item) {
      return item.options.filter((option) => {
        const select = option.querySelector('select');
        return select && !select.value;
      });
    }

    // Price shown before a size is chosen: the cheapest variant of the picked color.
    estimate(item) {
      const values = this.optionValues(item);
      const candidates = item.variants.filter((variant) =>
        variant.options.every((value, index) => !values[index] || value === values[index])
      );
      return candidates.reduce((best, variant) => (!best || variant.price < best.price ? variant : best), null);
    }

    showMissing(item, show) {
      const missing = show ? this.missingOptions(item) : [];
      item.options.forEach((option) => option.classList.toggle('has-error', missing.includes(option)));
      if (!item.chooseEl) return;
      const names = missing.map((option) => option.querySelector('label')?.textContent.trim().toLowerCase());
      item.chooseEl.textContent = missing.length ? `Please choose a ${names.join(' and ')} for the ${item.title}.` : '';
      item.chooseEl.hidden = !missing.length;
    }

    setActive(item, active) {
      item.active = active;
      if (item.toggle) item.toggle.checked = active;
      if (item.optionsBox) item.optionsBox.hidden = !active;
      item.el.classList.toggle('is-added', active);
      if (!active) this.showMissing(item, false);
      this.onOptionChange(item);
    }

    onOptionChange(item) {
      const values = this.optionValues(item);
      item.options.forEach((option, index) => {
        const label = option.querySelector('[data-om-option-value]');
        if (label) label.textContent = values[index];
      });

      const variant = this.variantOf(item);
      const shown = variant || this.estimate(item);
      if (shown && item.priceEl) item.priceEl.textContent = this.money(shown.price);
      if (shown?.image && item.imageEl) item.imageEl.src = shown.image;
      if (item.soldOutEl) item.soldOutEl.hidden = !item.active || !variant || variant.available;
      // A complaint about a missing size goes away as soon as it is chosen.
      if (item.chooseEl && !item.chooseEl.hidden) this.showMissing(item, true);
      this.showError('');
      this.refresh();
    }

    // Ticked items. `variant` is the exact one once every option is chosen;
    // until then an estimate keeps the total on the button meaningful.
    extras() {
      return this.items
        .filter((item) => item.active)
        .map((item) => ({ item, variant: this.variantOf(item) || this.estimate(item) }))
        .filter((entry) => entry.variant);
    }

    firstIncomplete() {
      return this.items.find((item) => item.active && this.missingOptions(item).length);
    }

    hasUnavailablePick() {
      return this.items.some((item) => item.active && !this.variantOf(item)?.available);
    }

    // ---- display ----
    money(cents) {
      try {
        return new Intl.NumberFormat(this.dataset.locale || 'en', {
          style: 'currency',
          currency: this.dataset.currency || 'USD',
        }).format(cents / 100);
      } catch (e) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }

    refresh() {
      const extras = this.extras();
      // The popup opens after this product went into the cart, so it counts as item 1.
      const count = 1 + extras.length;
      const subtotal = this.mainPrice + extras.reduce((sum, entry) => sum + entry.variant.price, 0);
      // Mirrors the store's automatic discount (set up in Shopify admin), which is
      // what actually changes the price in the cart and at checkout.
      const discounted = this.discountPercent > 0 && this.discountCount > 0 && count >= this.discountCount;
      // Rounded down like the cart does, so the popup never promises more than checkout gives.
      const saving = discounted ? Math.floor((subtotal * this.discountPercent) / 100) : 0;

      this.offers.forEach((offer) => offer.classList.toggle('is-done', count >= Number(offer.dataset.omOffer)));
      if (this.savingEl) {
        this.savingEl.hidden = !saving;
        this.savingEl.textContent = saving ? this.savingEl.dataset.text.replace('[amount]', this.money(saving)) : '';
      }

      // Greyed "Tick an item to add it" until something is ticked, then it names
      // what it adds: "Add tee & jacket".
      if (this.addButton && !this.busy) {
        const empty = !extras.length;
        this.addButton.classList.toggle('is-empty', empty);
        this.addButton.setAttribute('aria-disabled', String(empty));
        this.addButton.textContent = empty
          ? this.dataset.emptyLabel || ''
          : (this.dataset.addLabel || '').replace('[items]', this.tickedNames());
      }
    }

    // "tee", "tee & jacket", "tee, jacket & sweatshirt"
    tickedNames() {
      const names = this.items.filter((item) => item.active).map((item) => item.el.dataset.omShort);
      if (names.length < 3) return names.join(' & ');
      return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }

    // ---- step 1: the page's Add to cart ----
    onFormSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      // getAttribute, not form.id: the form's <input name="id"> shadows the id property.
      const formId = form.getAttribute('id');
      if (formId !== this.dataset.formId && formId !== `StickyAtcForm-${this.sectionId}`) return;
      if (this.shown || this.cartHasMatch) return; // Dawn's own flow: add, then the cart drawer

      const cart = document.querySelector('cart-drawer');
      const productForm = form.closest('product-form');
      const button = productForm?.querySelector('[type="submit"]');
      const mainId = Number(form.querySelector('[name="id"]')?.value);
      // Without a drawer to hand over to, or when Dawn would refuse the click anyway, stay out of it.
      if (!cart || !mainId || !button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
      this.addMain(mainId, productForm, button, cart);
    }

    request(items, cart) {
      const root = window.Shopify?.routes?.root || '/';
      return fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items,
          sections: cart.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      }).then((response) => response.json());
    }

    announce(response, variantId) {
      try {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: variantId, cartData: response });
        }
      } catch (e) {
        /* subscribers are optional */
      }
    }

    addMain(mainId, productForm, button, cart) {
      if (this.busy) return;
      this.busy = true;
      cart.setActiveElement(document.activeElement);
      // Same loading state Dawn's <product-form> shows.
      const spinner = productForm.querySelector('.loading__spinner');
      productForm.handleErrorMessage?.();
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      spinner?.classList.remove('hidden');

      this.request([{ id: mainId, quantity: 1 }], cart)
        .then((response) => {
          if (response.status) {
            productForm.handleErrorMessage?.(response.description || response.message);
            return;
          }
          this.announce(response, mainId);
          this.pending = { cart, response };
          this.shown = true;
          this.mainPrice = this.mainVariants.find((variant) => variant.id === mainId)?.price || this.mainPrice;
          this.busy = false;
          this.refresh();
          this.showError('');
          this.dialog.showModal();
          document.documentElement.classList.add('om-upsell-open');
        })
        .catch((error) => {
          console.error(error);
          // Could not add it here: hand the next click back to the page's normal flow.
          this.shown = true;
        })
        .finally(() => {
          this.busy = false;
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
          spinner?.classList.add('hidden');
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        });
    }

    // ---- step 2: the popup ----
    addExtras() {
      if (this.busy || !this.pending || !this.items.some((item) => item.active)) return;
      const incomplete = this.firstIncomplete();
      if (incomplete) {
        this.items.forEach((item) => this.showMissing(item, item.active));
        const select = this.missingOptions(incomplete)[0].querySelector('select');
        select.scrollIntoView({ behavior: 'smooth', block: 'center' });
        select.focus({ preventScroll: true });
        return;
      }
      if (this.hasUnavailablePick()) {
        this.showError('One of the items you ticked is sold out in that color or size. Please choose another, or untick it.');
        return;
      }

      const items = this.items
        .filter((item) => item.active)
        .map((item) => ({ id: this.variantOf(item).id, quantity: 1 }));
      const { cart } = this.pending;

      this.busy = true;
      this.showError('');
      this.addButton.setAttribute('aria-disabled', 'true');

      this.request(items, cart)
        .then((response) => {
          if (response.status) {
            this.showError(response.description || response.message || 'Could not add these items. Please try again.');
            return;
          }
          this.announce(response, items[0].id);
          // The drawer should show the cart as it is now, with everything in it.
          this.pending = { cart, response };
          this.cartHasMatch = true;
          this.closeUpsell();
        })
        .catch((error) => {
          console.error(error);
          this.showError('Could not add these items. Please check your connection and try again.');
        })
        .finally(() => {
          this.busy = false;
          this.refresh();
        });
    }

    closeUpsell() {
      if (this.dialog.open) this.dialog.close();
      this.afterUpsell();
    }

    // Every way out of the popup lands in the cart drawer. Safe to call twice.
    afterUpsell() {
      document.documentElement.classList.remove('om-upsell-open');
      if (!this.pending) return;
      this.reset();
      const { cart, response } = this.pending;
      this.pending = null;
      // The drawer reads `id` as the product just added.
      cart.renderContents({ ...response, id: response.items?.[0]?.id });
    }

    // Untick everything so nothing lingers behind the closed popup.
    reset() {
      this.items.forEach((item) => {
        if (item.active) this.setActive(item, false);
        // Sizes go back to "Choose size".
        item.options.forEach((option) => {
          const select = option.querySelector('select');
          if (select) select.selectedIndex = 0;
        });
      });
      this.refresh();
    }
  }

  customElements.define('om-bundle-builder', OmBundleBuilder);
}

if (!customElements.get('om-proof-quote')) {
  /**
   * <om-proof-quote>: rotates the quote under the star rating
   * (see snippets/om-proof-quote.liquid). The first quote is in the markup; the
   * others come from the JSON list the snippet prints from the block settings.
   */
  class OmProofQuote extends HTMLElement {
    connectedCallback() {
      this.seconds = parseFloat(this.dataset.rotateSeconds) || 0;
      this.figure = this.querySelector('[data-om-quote-figure]');
      this.textEl = this.querySelector('[data-om-quote-text]');
      this.authorEl = this.querySelector('[data-om-quote-author]');
      if (!this.seconds || !this.figure || !this.textEl || !this.authorEl) return;

      let extra = [];
      try {
        extra = JSON.parse(this.querySelector('[data-om-quotes]')?.textContent || '[]');
      } catch (e) {
        extra = [];
      }
      this.quotes = [{ text: this.textEl.textContent, author: this.authorEl.textContent }].concat(
        extra.filter((quote) => quote.text).map((quote) => ({ text: `\u201c${quote.text}\u201d`, author: quote.author || '' }))
      );
      if (this.quotes.length < 2) return;
      this.index = 0;

      // Reserve the tallest quote's height so the price below never jumps;
      // text wraps differently once the web font loads or the width changes.
      this.reserveHeight = this.reserveHeight.bind(this);
      this.reserveHeight();
      document.fonts?.ready.then(this.reserveHeight);
      window.addEventListener('resize', this.reserveHeight);

      this.onVisibility = () => (document.hidden ? this.stop() : this.start());
      document.addEventListener('visibilitychange', this.onVisibility);
      // Reading it? Hold it still.
      this.addEventListener('pointerenter', () => this.stop());
      this.addEventListener('pointerleave', () => this.start());
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      window.removeEventListener('resize', this.reserveHeight);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    show(quote) {
      this.textEl.textContent = quote.text;
      this.authorEl.textContent = quote.author;
    }

    reserveHeight() {
      let tallest = 0;
      this.figure.style.minHeight = '';
      this.quotes.forEach((quote) => {
        this.show(quote);
        tallest = Math.max(tallest, this.figure.offsetHeight);
      });
      this.show(this.quotes[this.index]);
      this.figure.style.minHeight = `${tallest}px`;
    }

    start() {
      if (this.timer) return;
      this.timer = window.setInterval(() => this.next(), this.seconds * 1000);
    }

    stop() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    next() {
      this.index = (this.index + 1) % this.quotes.length;
      // Fade out, swap, fade in (the CSS drops the fade for reduced motion).
      this.figure.classList.add('is-changing');
      window.setTimeout(() => {
        this.show(this.quotes[this.index]);
        this.figure.classList.remove('is-changing');
      }, 250);
    }
  }

  customElements.define('om-proof-quote', OmProofQuote);
}

if (!customElements.get('om-parts-checklist')) {
  class OmPartsChecklist extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll('[data-om-part]'));
      this.countEl = this.querySelector('[data-om-count]');
      this.unitEl = this.querySelector('[data-om-unit]');
      this.verdictEl = this.querySelector('[data-om-verdict]');
      this.lineEl = this.querySelector('[data-om-line]');
      try {
        this.levels = JSON.parse(this.querySelector('[data-om-levels]')?.textContent || '[]');
      } catch (e) {
        this.levels = [];
      }

      this.buttons.forEach((button) =>
        button.addEventListener('click', () => {
          button.setAttribute('aria-pressed', String(button.getAttribute('aria-pressed') !== 'true'));
          this.render();
        })
      );
      this.querySelector('[data-om-buy]')?.addEventListener('click', () => {
        const target = document.querySelector('product-info .product-form__submit');
        if (!target) return;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.focus({ preventScroll: true });
      });
      this.render();
    }

    render() {
      const count = this.buttons.filter((button) => button.getAttribute('aria-pressed') === 'true').length;
      // Levels are sorted by their minimum count; the last one reached wins.
      const level = this.levels.filter((entry) => count >= entry.min).pop();
      if (this.countEl) this.countEl.textContent = count;
      if (this.unitEl) this.unitEl.textContent = count === 1 ? this.unitEl.dataset.one : this.unitEl.dataset.many;
      if (level && this.verdictEl) this.verdictEl.textContent = level.title;
      if (level && this.lineEl) this.lineEl.textContent = level.text;
    }
  }

  customElements.define('om-parts-checklist', OmPartsChecklist);
}
